import { useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Ban, ExternalLink } from "lucide-react";

import { isNotFound } from "@/api/client";
import { cancelDeployment, getTenant, listDeployments } from "@/api/tenants";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DeploymentTimeline } from "@/components/DeploymentTimeline";
import { MenuItem } from "@/components/KebabMenu";
import { RelativeTime } from "@/components/RelativeTime";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { TenantNotFound } from "@/components/TenantNotFound";
import { DeploymentStatusBadge, deploymentStatusLabel } from "@/components/StatusBadge";
import {
  POLL_INTERVAL_MS,
  inflightCount,
  isActiveStatus,
  totalDuration,
} from "@/lib/deployments";
import type { Deployment } from "@/types/api";
import {
  Button,
  EmptyState,
  Icon,
  Notice,
  Page,
  PageHeader,
  StatusDot,
  Table,
  type TableColumn,
} from "@/components/ui";

export function TenantDeploymentsPage() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token ?? "";

  // Which runs have their detail row open, and which one the operator is about
  // to mark as failed. Held HERE, not in a row component: the table draws the rows.
  // The run to cancel is kept by its ID, never as a copy of the row: this list polls,
  // and a copy would go on saying "in progress" about a run that has since finished.
  const [openRuns, setOpenRuns] = useState<ReadonlySet<string>>(new Set());
  const [cancelRunId, setCancelRunId] = useState<string | null>(null);

  const tenantQuery = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId,
  });

  const query = useQuery({
    queryKey: ["deployments", tenantId],
    queryFn: () => listDeployments(token, tenantId, { limit: 50 }),
    enabled: !!token && !!tenantId,
    refetchInterval: (q) =>
      inflightCount(q.state.data?.items ?? []) > 0 ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

  const cancelMutation = useMutation({
    mutationFn: (runId: string) => cancelDeployment(token, tenantId, runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployments", tenantId] });
      setCancelRunId(null);
    },
  });

  function toggleOpen(runId: string) {
    setOpenRuns((open) => {
      const next = new Set(open);
      if (!next.delete(runId)) next.add(runId);
      return next;
    });
  }

  // Only an error with nothing to show empties the table (``loadFailure``): this
  // list polls while a run is in flight, and a failed poll must not unmount the
  // rows — and the open "Cancel this deployment" modal with them.
  const items = query.data?.items;
  const inflight = inflightCount(items ?? []);

  // The run the modal is about, as the list says it is NOW: it may have finished since the
  // modal opened, or dropped out of the list. Cancelling it then would mark a finished run as
  // failed, so the modal says so and locks the button instead.
  const cancelTarget = cancelRunId ? items?.find((d) => d.runId === cancelRunId) : undefined;
  const stillCancellable = cancelTarget !== undefined && isActiveStatus(cancelTarget.status);

  const columns: TableColumn<Deployment>[] = [
    { key: "status", header: "Status", cell: (d) => <DeploymentStatusBadge status={d.status} /> },
    { key: "stage", header: "Stage", cell: (d) => <span className="font-medium">{d.stage}</span> },
    { key: "type", header: "Type", muted: true, cell: (d) => (d.kind === "destroy" ? "Destroy" : "Deploy") },
    { key: "triggered", header: "Triggered", muted: true, cell: (d) => <RelativeTime epochMs={d.triggeredAt} /> },
    { key: "duration", header: "Duration", mono: true, cell: (d) => totalDuration(d) },
    {
      key: "log",
      header: "Log",
      cell: (d) =>
        d.ghaRunUrl ? (
          <a
            href={d.ghaRunUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-600 hover:underline"
          >
            View log
            <Icon icon={ExternalLink} size="sm" />
          </a>
        ) : (
          <span className="text-ink-muted">—</span>
        ),
    },
    {
      key: "details",
      header: "Details",
      cell: (d) =>
        d.events.length > 0 || d.errorMessage ? (
          <Button variant="ghost" size="sm" aria-expanded={openRuns.has(d.runId)} onClick={() => toggleOpen(d.runId)}>
            {openRuns.has(d.runId) ? "Hide" : "Details"}
          </Button>
        ) : (
          <span className="text-xs text-ink-muted">No details</span>
        ),
    },
  ];

  // No such tenant: there is no history to show, and the list below would only fail in its own way.
  if (tenantQuery.isLoadingError && isNotFound(tenantQuery.error)) return <TenantNotFound tenantId={tenantId} />;

  return (
    <Page>
      <RefreshFailed query={query} className="mb-6" />
      <PageHeader
        back={{ to: `/tenants/${tenantId}`, label: tenantQuery.data?.metadata.displayName ?? tenantId }}
        title="Deployment history"
        subtitle={
          items && (
            <>
              {items.length} operation{items.length === 1 ? "" : "s"}
              {inflight > 0 && (
                <>
                  {" · "}
                  <span className="inline-flex items-center gap-1.5 text-info-fg">
                    <StatusDot tone="info" pulse />
                    {inflight} in progress
                  </span>
                </>
              )}
            </>
          )
        }
      />

      <Table
        label="Deployments"
        columns={columns}
        rows={items}
        rowKey={(d) => d.runId}
        error={loadFailure(query)}
        empty={<EmptyState title="No deployments for this tenant" />}
        detail={(d) =>
          openRuns.has(d.runId) ? (
            <>
              {d.errorMessage && (
                <Notice tone="danger" title="Failure details" className="mb-4">
                  <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">
                    {d.errorMessage}
                  </pre>
                </Notice>
              )}
              <DeploymentTimeline deployment={d} />
            </>
          ) : null
        }
        actions={{
          label: (d) => `Actions for the ${d.kind} of ${d.stage} (${d.runId})`,
          items: (d) =>
            isActiveStatus(d.status) ? (
              <MenuItem
                danger
                icon={Ban}
                disabled={cancelMutation.isPending && cancelMutation.variables === d.runId}
                title="Mark this stuck deployment as failed (escape hatch)"
                onClick={() => setCancelRunId(d.runId)}
              >
                Cancel deployment…
              </MenuItem>
            ) : null,
        }}
      />

      {cancelRunId && (
        <ConfirmModal
          title="Cancel this deployment"
          confirmLabel="Mark as failed"
          pendingLabel="Cancelling…"
          danger
          pending={cancelMutation.isPending}
          confirmDisabled={!stillCancellable}
          error={cancelMutation.error}
          onCancel={() => {
            cancelMutation.reset();
            setCancelRunId(null);
          }}
          onConfirm={() => {
            if (stillCancellable) cancelMutation.mutate(cancelRunId);
          }}
        >
          {cancelTarget === undefined ? (
            <p>This run is no longer in the list, so there is nothing left to cancel.</p>
          ) : !stillCancellable ? (
            <p>
              This {cancelTarget.kind === "destroy" ? "destroy" : "deployment"} run on{" "}
              <strong>{cancelTarget.stage}</strong> has already finished (
              {deploymentStatusLabel(cancelTarget.status).toLowerCase()}), so there is nothing left to
              cancel.
            </p>
          ) : (
            <p>
              Marks this{" "}
              {cancelTarget.kind === "destroy" ? "destroy" : "deployment"} run
              {" "}on <strong>{cancelTarget.stage}</strong> (triggered{" "}
              <RelativeTime epochMs={cancelTarget.triggeredAt} />) as failed, to unblock
              the next one. Only use this when the run never started or
              appears stuck.
            </p>
          )}
        </ConfirmModal>
      )}
    </Page>
  );
}
